import { z } from 'zod';
import { FINANCIAL_PRODUCT } from '../constants';
import { requiredString, validateE2515HouseholdData } from '../helper';
import {
  address,
  baseBusinessApplicant,
  baseResidentialApplicant,
  businessDetails,
  capacityToPayAgreement,
  generateSchema,
  householdSchema,
  ownershipDetails,
  repaymentAccount,
  repaymentAccountSource,
  repaymentTerms,
} from '../shared';
import { baseApplicationStep } from './baseApplication';
import { otherApplicantPropertiesSchema, termsAndConditionsSchema } from './createApplication';
import { identificationSchema } from './identification';
import { applicationIncomeSchema } from './incomeVerification';
import { otherJointApplicantPropertiesSchema } from './jointEligibility';
import { repaymentAccountSchema } from './repaymentAccount';

/**
 * Schema to validate the complete application data in application service
 * it is a combination of the base application schema and the custom application schema
 * based on the financial product
 *
 */
const baseApplicationSchema = baseApplicationStep
  .pick({
    id: true,
    status: true,
  })
  .merge(
    z.object({
      address: address,
      repaymentTerm: repaymentTerms,
    }),
  );

const defaultApplicationSchema = {
  payment: repaymentAccount,
  hasJointApplicant: z.boolean(),
};

const enterpriseApplicationSchema = {
  payment: repaymentAccount,
  enterprise: businessDetails.and(ownershipDetails),
};

const flexApplicationSchema = {
  payment: repaymentAccount.and(repaymentAccountSource),
  hasJointApplicant: z.boolean(),
};

const electrify2515ApplicationSchema = {
  payment: z.object({
    isCashApplication: z.boolean(),
    methodId: z.string().optional(),
  }),
  hasJointApplicant: z.boolean().optional(),
  packageId: z.string(),
};

const customApplicationSchemas = {
  [FINANCIAL_PRODUCT.ACT_SFA]: enterpriseApplicationSchema,
  [FINANCIAL_PRODUCT.FLEX]: flexApplicationSchema,
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: electrify2515ApplicationSchema,
};

export const applicationSchema = generateSchema<typeof customApplicationSchemas, typeof defaultApplicationSchema>({
  customSchemas: customApplicationSchemas,
  defaultSchema: defaultApplicationSchema,
})
  .and(baseApplicationSchema)
  .superRefine((data, ctx) => {
    if (data.financialProductId === FINANCIAL_PRODUCT.ELECTRIFY_2515) {
      if (data.payment.isCashApplication === false && !data.payment.methodId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Repayment account is required for loan application',
          path: ['payment', 'methodId'],
        });
      }
    }
  });

/**
 * Schema to validate the complete applicant data (residential, enterprise, joint) in applicant service
 * it is a combination of the base applicant schema and the custom applicant schema
 * based on the financial product
 *
 */

// change the field type to string instead of date for validation purposes
const dobToString = z.object({
  applicant: z.object({
    dateOfBirth: requiredString(),
  }),
});

// change the field type to string instead of date for validation purposes
const expiryDateToString = z.object({
  applicant: z.object({
    identification: z.object({
      passport: z
        .object({
          expiryDate: requiredString(),
        })
        .optional(),
      medicare: z
        .object({
          expiryDate: requiredString(),
        })
        .optional(),
      drivingLicense: z
        .object({
          expiryDate: requiredString(),
        })
        .optional(),
    }),
  }),
});

const defaultResidentialApplicantSchema = {
  applicant: baseResidentialApplicant,
};

const flexApplicantSchema = {
  applicant: baseResidentialApplicant.merge(
    z.object({
      termsAndConditions: capacityToPayAgreement,
    }),
  ),
};

const customApplicantSchemas = {
  [FINANCIAL_PRODUCT.FLEX]: flexApplicantSchema,
};

export const residentialApplicantSchema = generateSchema<
  typeof customApplicantSchemas,
  typeof defaultResidentialApplicantSchema
>({
  customSchemas: customApplicantSchemas,
  defaultSchema: defaultResidentialApplicantSchema,
})
  .and(
    z.object({
      financialProductId: z.any(),
      isCashApplication: z.boolean().optional(),
    }),
  )
  .and(identificationSchema)
  .and(applicationIncomeSchema)
  .superRefine((data, ctx) => {
    if (
      data.applicant.usingOpenBanking &&
      (!data.applicant.income?.primary?.amount || !data.applicant.income?.primary?.period)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please complete income verification',
        path: ['applicant', 'income', 'primary'],
      });
    }
  })
  .and(householdSchema)
  .superRefine((data, ctx) => {
    if (data.financialProductId === FINANCIAL_PRODUCT.ELECTRIFY_2515 && typeof data.isCashApplication === 'boolean') {
      validateE2515HouseholdData(data, ctx, data.isCashApplication);
    }
  })
  .and(repaymentAccountSchema)
  .and(termsAndConditionsSchema)
  .superRefine((data, ctx) => {
    if (data.financialProductId === FINANCIAL_PRODUCT.ELECTRIFY_2515 && data.isCashApplication === false) {
      if (!data.applicant.termsAndConditions.directDebitStatement) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Direct debit statement is required',
          path: ['applicant', 'termsAndConditions', 'directDebitStatement'],
        });
      }

      if (!data.applicant.termsAndConditions.authoriseBrighteStatement) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Authorise Brighte statement is required',
          path: ['applicant', 'termsAndConditions', 'authoriseBrighteStatement'],
        });
      }
    }
  })
  .and(otherApplicantPropertiesSchema)
  .and(dobToString)
  .and(expiryDateToString);

export const enterpriseApplicantSchema = z
  .object({
    financialProductId: z.literal(FINANCIAL_PRODUCT.ACT_SFA),
    applicant: baseBusinessApplicant,
  })
  .and(repaymentAccountSchema)
  .and(termsAndConditionsSchema);

export const residentialJointApplicantSchema = z
  .object(defaultResidentialApplicantSchema)
  .and(identificationSchema)
  .and(applicationIncomeSchema)
  .and(termsAndConditionsSchema)
  .and(otherJointApplicantPropertiesSchema)
  .and(dobToString)
  .and(expiryDateToString);

export const addedJointApplicantSchema = baseResidentialApplicant.pick({
  id: true,
  status: true,
  type: true,
  firstName: true,
  lastName: true,
  email: true,
  phone: true,
});
